/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty, isObject, omit } from 'lodash'

import type {
  ElasticsearchClusterPlan,
  DeploymentUpdateRequest,
  ElasticsearchClusterTopologyElement,
  ElasticsearchPayload,
  ElasticsearchSystemSettings,
  Extension,
} from '@modules/cloud-api/v1/types'
import type { StackDeployment } from '@modules/ui-types'

import { getAutoscalingStatus } from '@/lib/stackDeployments/selectors/autoscaling'
import { getFirstEsCluster, getEsPlan } from '@/lib/stackDeployments/selectors/stackDeployment'
import { isSystemOwned } from '@/lib/stackDeployments/selectors/metadata'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { getUpsertVersion } from '@/lib/stackDeployments/selectors/creates'

import { diff, gte, lt } from '../semver'
import { replaceIn, updateIn } from '../immutability-helpers'
import { getPlanWithUpdatedCustomPlugins, getIncompatibleCustomPlugins } from '../plugins'
import { strategies } from '../clusterStrategies'
import { preparePlanBeforeUpdateCluster } from '../clusters'

import { sanitizeUpdateRequestBeforeSend } from './updates'

type SettingsUpdater = (settings: ElasticsearchSystemSettings) => ElasticsearchSystemSettings

export function isMajorVersionChange(previous?: string | null, current?: string | null): boolean {
  if (!previous || !current) {
    return false
  }

  return diff(previous, current) === `major`
}

export function getUpdateRequestWithPluginsAndTransforms({
  updateRequest,
  deploymentUnderEdit,
  extensions,
  selectedPluginUrls,
}: {
  updateRequest: DeploymentUpdateRequest
  deploymentUnderEdit: StackDeployment
  extensions: Extension[]
  selectedPluginUrls: string[]
}): DeploymentUpdateRequest {
  const version = getUpsertVersion({ deployment: updateRequest }) as string

  const updateRequestWithTransforms = getUpdateRequestWithTransforms({
    updateRequest,
    deploymentUnderEdit,
    version,
  })

  const updateRequestWithPlugins = getUpdateRequestWithPlugins({
    updateRequest: updateRequestWithTransforms,
    deploymentUnderEdit,
    extensions,
    selectedPluginUrls,
  })

  const sanitizedUpdateRequest = sanitizeUpdateRequestBeforeSend({
    deployment: updateRequestWithPlugins,
  })

  return sanitizedUpdateRequest
}

function getUpdateRequestWithTransforms({
  updateRequest,
  deploymentUnderEdit,
  version,
}: {
  updateRequest: DeploymentUpdateRequest
  deploymentUnderEdit: StackDeployment
  version: string
}): DeploymentUpdateRequest {
  return replaceIn(
    updateRequest,
    ['resources', 'elasticsearch', '0'],
    getVersionUpdateTransforms({
      deployment: updateRequest,
      deploymentUnderEdit,
      version,
    }),
  )
}

function getUpdateRequestWithPlugins({
  updateRequest,
  deploymentUnderEdit,
  extensions,
  selectedPluginUrls,
}: {
  updateRequest: DeploymentUpdateRequest
  deploymentUnderEdit: StackDeployment
  extensions: Extension[]
  selectedPluginUrls: string[]
}): DeploymentUpdateRequest {
  // Not expected null here
  const plan = getEsPlan({ deployment: updateRequest }) as ElasticsearchClusterPlan
  const version = getVersion({ deployment: deploymentUnderEdit })
  const pendingVersion = getUpsertVersion({ deployment: updateRequest })
  const removedPlugins = getIncompatibleCustomPlugins(version, pendingVersion, plan)
  const newPlan = getPlanWithUpdatedCustomPlugins(
    selectedPluginUrls,
    removedPlugins,
    extensions,
    plan,
  )

  const planOverrides = {
    transient: {
      strategy: strategies.autodetect,
    },
  }

  // apply same fancy transformations that `saveClusterPlan` would
  const esPlan = preparePlanBeforeUpdateCluster({
    plan: newPlan,
    planOverrides,
    autoscalingStatus: getAutoscalingStatus({ deployment: updateRequest }),
  })

  const updatedDeploymentRequest = replaceIn(
    updateRequest,
    ['resources', 'elasticsearch', '0', 'plan'],
    esPlan,
  )

  return updatedDeploymentRequest
}

function getVersionUpdateTransforms({
  deployment,
  deploymentUnderEdit,
  version,
}: {
  deployment: DeploymentUpdateRequest
  deploymentUnderEdit: StackDeployment
  version: string
}): ElasticsearchPayload {
  // Not expected null here
  const esCluster = getFirstEsCluster({ deployment }) as ElasticsearchPayload
  const currentVersion = getVersion({ deployment: deploymentUnderEdit })

  if (!currentVersion) {
    return esCluster
  }

  if (!isMajorVersionChange(currentVersion, version)) {
    const systemOwned = isSystemOwned({ deployment: deploymentUnderEdit })

    if (gte(version, `5.0.0`) && lt(version, `6.0.0`) && systemOwned) {
      return upgradeSystemClusterPlanTo5xx(esCluster)
    }

    return esCluster
  }

  // We're performing a major upgrade

  if (gte(version, `6.0.0`)) {
    if (lt(currentVersion, `6.0.0`)) {
      return upgradePlanTo6x(esCluster)
    }

    return esCluster
  }

  if (gte(version, `5.0.0`)) {
    if (lt(currentVersion, `5.0.0`)) {
      return upgradePlanTo5x(esCluster)
    }

    return esCluster
  }

  return esCluster
}

function upgradePlanTo5x(esCluster: ElasticsearchPayload): ElasticsearchPayload {
  return updateSystemSettings(esCluster, (settings) => ({
    ...omit(settings, [`default_shards_per_index`]),
    scripting: {
      file: settings.scripting?.file,
      inline: settings.scripting?.inline,
      stored: settings.scripting?.stored,
      expressions_enabled: true,
      mustache_enabled: true,
      painless_enabled: true,
    },
    watcher_trigger_engine: `scheduler`,
    monitoring_collection_interval: -1,
    reindex_whitelist: settings.reindex_whitelist ?? [`*.io:*`, `*.com:*`],
  }))
}

// We need to prepare system clusters for upgrading to 6 and above where scripting is required
// this makes system cluster scripting settings consistent between clusters created ECE 1.x
// and clusters created more recently. See https://github.com/elastic/cloud/issues/28402
function upgradeSystemClusterPlanTo5xx(esCluster: ElasticsearchPayload): ElasticsearchPayload {
  return updateSystemSettings(esCluster, (settings) => {
    const enableScripting = {
      inline: { enabled: true },
      stored: { enabled: true },
    }

    const oldSettings = isObject(settings) ? settings : {}
    const oldScripting = isObject(oldSettings.scripting) ? oldSettings.scripting : {}

    return {
      ...oldSettings,
      scripting: {
        ...oldScripting,
        ...enableScripting,
      },
    }
  })
}

/**
 * Scripting config has changed a lot in 6.x, and so the defaults have too.
 * @param {Object} cluster the pending cluster state
 */
function upgradePlanTo6x(esCluster: ElasticsearchPayload): ElasticsearchPayload {
  const updatedSettings = updateSystemSettings(esCluster, (settings) => ({
    ...settings,
    scripting: {
      inline: { enabled: true },
      stored: { enabled: true },
    },
  }))

  const isSystemCluster = Boolean(esCluster.settings?.metadata?.system_owned)

  if (isSystemCluster) {
    // there are legacy scripting settings that are set in `user_settings_json`
    // when system clusters are created that don't work on 6.x, so we must remove them
    return updateElasticsearchSegment(updatedSettings, (elasticsearch) => ({
      ...elasticsearch,
      user_settings_json: {},
    }))
  }

  return updatedSettings
}

function updateSystemSettings(
  esCluster: ElasticsearchPayload,
  updater: SettingsUpdater,
): ElasticsearchPayload {
  if (isEmpty(esCluster.plan.cluster_topology)) {
    return updateIn(esCluster, [`plan`, `elasticsearch`, `system_settings`], updater)
  }

  return esCluster.plan.cluster_topology.reduce(
    (
      updatedCluster: ElasticsearchPayload,
      _nodeConfiguration: ElasticsearchClusterTopologyElement,
      index: number,
    ) => updateIn(updatedCluster, getSettingsPath(index), updater),
    esCluster,
  )

  function getSettingsPath(index: number): string[] {
    return [`plan`, `cluster_topology`, `${index}`, `elasticsearch`, `system_settings`]
  }
}

function updateElasticsearchSegment(
  esCluster: ElasticsearchPayload,
  updater: SettingsUpdater,
): ElasticsearchPayload {
  if (isEmpty(esCluster.plan.cluster_topology)) {
    return updateIn(esCluster, [`plan`, `elasticsearch`], updater)
  }

  return esCluster.plan.cluster_topology.reduce(
    (
      updatedCluster: ElasticsearchPayload,
      _nodeConfiguration: ElasticsearchClusterTopologyElement,
      index: number,
    ) => updateIn(updatedCluster, getSettingsPath(index), updater),
    esCluster,
  )

  function getSettingsPath(index: number): string[] {
    return [`plan`, `cluster_topology`, `${index}`, `elasticsearch`]
  }
}

// Our cloud API endpoint uses xpack deprecations when they were introduced
// https://www.elastic.co/guide/en/elasticsearch/reference/5.6/migration-api-deprecation.html
const XPACK_DEPRECATIONS_SUPPORTED = `5.6.0`
// And then we make use of the kibana upgrade assistant, but don't block on it.
// https://www.elastic.co/guide/en/kibana/6.7/upgrade-assistant.html
const KIBANA_UPGRADE_ASSISTANT_SUPPORTED = `6.7.0`
// In 8.6.0 the upgrade assistant become available for all version upgrades, not just major versions
const KIBANA_UPGRADE_ASSISTANT_AVAILABLE_FOR_ALL_UPGRADES = `8.6.2`

export function isUpgradeAssistantSupported(version: string | null): boolean {
  return Boolean(version && gte(version, XPACK_DEPRECATIONS_SUPPORTED))
}

export function isUpgradeAssistantUsingKibana(version: string | null): boolean {
  return Boolean(version && gte(version, KIBANA_UPGRADE_ASSISTANT_SUPPORTED))
}

function isKibanaUpgradeAssistantAvailableForNonMajorUpgrades(version: string | null): boolean {
  return Boolean(version && gte(version, KIBANA_UPGRADE_ASSISTANT_AVAILABLE_FOR_ALL_UPGRADES))
}

export function shouldUpgradeAssistantBeUsed(
  oldVersion: string | null,
  newVersion: string | null,
): boolean {
  if (!oldVersion || !newVersion) {
    return false
  }

  // During kibana 8.17.0 release, a blocker (https://github.com/elastic/kibana-team/issues/1397)
  // was found when upgrading from 8.17.0 to 8.17.1. As a result, we decided to skip
  // the upgrading assistant until kibana fixes the issue below
  // https://github.com/elastic/kibana-team/issues/1397
  if (gte(oldVersion, '8.17.0') && lt(oldVersion, '8.18.0')) {
    return false
  }

  if (
    isMajorVersionChange(oldVersion, newVersion) ||
    isKibanaUpgradeAssistantAvailableForNonMajorUpgrades(oldVersion)
  ) {
    return true
  }

  return false
}
